import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, StatusPill, Table, useTable } from "@repo/o37-ui";
import { Settings, Share } from "lucide-react";

interface VDRRoom {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  vdr_permissions?: { access_level: string }[];
}

interface VDRListTableProps {
  rooms: VDRRoom[];
  userRole: string;
  onShare: (roomId: string) => void;
  onSettings: (roomId: string) => void;
}

export default function VDRListTable({ rooms, userRole, onShare, onSettings }: VDRListTableProps) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  const columns = useMemo(() => [
    {
      id: "id",
      header: "ID",
      accessorFn: (row: VDRRoom) => row.id,
      cell: ({ row }: { row: { original: VDRRoom } }) => (
        <span className="font-mono text-xs text-neutral-500">
          {(row.original.id || '').slice(0, 8)}...
        </span>
      )
    },
    {
      id: "name",
      header: "Name",
      accessorFn: (row: VDRRoom) => row.name,
      cell: ({ row }: { row: { original: VDRRoom } }) => (
        <Link 
            to={`/fundraising/vdr/${row.original.id}/settings`}
            className="font-medium hover:underline decoration-dotted underline-offset-2 decoration-neutral-400 text-neutral-900"
        >
            {row.original.name}
        </Link>
      )
    },
    {
      id: "slug",
      header: "Slug",
      accessorFn: (row: VDRRoom) => row.slug,
      cell: ({ row }: { row: { original: VDRRoom } }) => (
        <span className="text-neutral-500 font-mono text-xs">
            {row.original.slug || '-'}
        </span>
      )
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row: VDRRoom) => row.status,
      cell: ({ row }: { row: { original: VDRRoom } }) => (
        <StatusPill s={row.original.status || 'active'} />
      )
    },
    {
      id: "role",
      header: "Role",
      cell: ({ row }: { row: { original: VDRRoom } }) => {
        const item = row.original;
        const accessLevel = item.vdr_permissions?.[0]?.access_level || 'Viewer';
        return (
            <span className="text-neutral-500 capitalize">
                {userRole === 'super_admin' ? 'Super Admin' : accessLevel}
            </span>
        );
      }
    },
    {
      id: "createdAt",
      header: "Created At",
      accessorFn: (row: VDRRoom) => row.created_at,
      cell: ({ row }: { row: { original: VDRRoom } }) => (
        <span className="text-neutral-500">
            {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString() : '-'}
        </span>
      )
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
          headerClassName: "text-right",
          cellClassName: "justify-end text-right"
      },
      cell: ({ row }: { row: { original: VDRRoom } }) => {
        const item = row.original;
        const accessLevel = item.vdr_permissions?.[0]?.access_level || 'Viewer';
        const canManage = userRole === 'super_admin' || accessLevel === 'admin';

        if (!canManage) return null;

        return (
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            <Button 
                variant="outline"
                className="h-8 text-xs px-3"
                onClick={() => onShare(item.id)}
                text="Share"
                icon={<Share className="w-3 h-3" />}
            />
            <Button 
                variant="outline"
                className="h-8 text-xs px-3"
                onClick={() => onSettings(item.id)}
                text="Settings"
                icon={<Settings className="w-3 h-3" />}
            />
          </div>
        );
      }
    }
  ], [userRole, onShare, onSettings]);

  const { table, ...tableProps } = useTable({
    data: rooms,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: rooms.length,
    resourceName: (plural) => `project${plural ? "s" : ""}`,
  });

  return (
    <Card>
       <Table {...tableProps} table={table} />
    </Card>
  );
}






